import {useEffect, useState} from "react";
import {
    AppBar,
    Box,
    Button,
    ButtonBase,
    CssBaseline,
    Divider,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Toolbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from '@mui/icons-material/Language';
import HelpIcon from '@mui/icons-material/Help';
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import { signOut } from "aws-amplify/auth";

interface Props {
    customWindow?: () => Window;
    user: any;
    children: React.ReactElement<unknown>;
}

const drawerWidth = 240;

export const NavBar = ({customWindow, user, children}: Props) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [language, setLanguage] = useState(i18n.language);
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleDrawerToggle = () => {
        setMobileOpen((prevState) => !prevState);
    };

    useEffect(() => {
        const savedLanguage = localStorage.getItem('language');
        if (savedLanguage) {
            i18n.changeLanguage(savedLanguage);
            setLanguage(savedLanguage);
        }
    }, [i18n]);

    const handleLngChange = (event: SelectChangeEvent) => {
        const newLanguage = event.target.value;
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);
        localStorage.setItem('language', newLanguage);  // Speichern der Sprache im localStorage
    };
    const handleLngChangeDrawer = async (
        _event: React.MouseEvent<HTMLElement>,
        lng: string,
    ) => {
        setLanguage(lng);
        await i18n.changeLanguage(lng);
    };

    const handleSignOut = async () => {
        if (window.confirm(t('navBar.logoutAlert'))) {
            await signOut();
            navigate("/login", { replace: true });
        }
    };

    const drawer = (
        <Box onClick={handleDrawerToggle} sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <Typography variant="h6" sx={{my: 2}}>
                {"KMG-VISION.cloud \n Creator"}
            </Typography>
            <Divider/>
            <List>
                <ListItem key={'dashboard'} disablePadding>
                    <ListItemButton sx={{textAlign: 'center'}} onClick={() => navigate('/')}>
                        <ListItemText primary={"Dashboard"}/>
                    </ListItemButton>
                </ListItem>
                <ListItem key={'privacypolicy'} disablePadding>
                    <ListItemButton sx={{textAlign: 'center'}} onClick={() => navigate('/privacypolicy')}>
                        <ListItemText primary={t('navBar.privacyPolicy')}/>
                    </ListItemButton>
                </ListItem>
                <ListItem key={'imprint'} disablePadding>
                    <ListItemButton sx={{textAlign: 'center'}} onClick={() => navigate('/imprint')}>
                        <ListItemText primary={t('navBar.imprint')}/>
                    </ListItemButton>
                </ListItem>
                <ListItem key={'signOut'} disablePadding>
                    <ListItemButton sx={{textAlign: 'center'}} onClick={handleSignOut}>
                        <ListItemText primary={t('navBar.logout')}/>
                    </ListItemButton>
                </ListItem>
            </List>
            <ToggleButtonGroup
                value={language}
                exclusive
                onChange={handleLngChangeDrawer}
                aria-label="text alignment"
            >
                <ToggleButton value="en" aria-label="left aligned">
                    EN
                </ToggleButton>
                <ToggleButton value="de" aria-label="centered">
                    DE
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );

    const container = customWindow !== undefined ? () => customWindow().document.body : undefined;

    return (
        <Box sx={{ display: 'flex', flexDirection: "column", height: "100vh" }}>
            <CssBaseline />
            <AppBar position="fixed" style={{backgroundColor: '#53b2ef'}} component="nav">
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{mr: 2, display: {sm: 'none'}}}
                    >
                            <MenuIcon/>
                    </IconButton>
                    <Typography
                        aria-pressed={true}
                        variant="h6"
                        component="div"
                        sx={{flexGrow: 1, display: {xs: 'none', sm: 'block'}}}
                    >
                        <ButtonBase style={{color: 'white'}} onClick={() => navigate('/')}>
                            {"KMG-VISION.cloud \n Creator"}
                        </ButtonBase>
                    </Typography>
                    <Box sx={{
                        display: {
                            xs: 'none',
                            sm: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }
                    }}>
                        <Button onClick={() => window.open('https://www.gte.de/datenschutz/ds-creator-kmg-vision-cloud')} sx={{color: '#fff'}}>
                            {t('navBar.privacyPolicy')}
                        </Button>
                        <Button onClick={() => window.open('https://www.gte.de/impressum')} sx={{color: '#fff'}}>
                            {t('navBar.imprint')}
                        </Button>
                        <Button onClick={()=> user? handleSignOut() : navigate('/login')} sx={{color: '#fff'}}>
                            {user? t('navBar.logout') : t('navBar.login')}
                        </Button>
                        <Select displayEmpty
                                variant="standard"
                                disableUnderline
                                value={language}
                                onChange={handleLngChange}
                                sx={{color: 'white'}}
                                renderValue={(value) => {
                                    return (
                                        <Box sx={{display: "flex", gap: 1, color: 'white'}}>
                                            <LanguageIcon style={{color: 'white'}}/>
                                            {value}
                                        </Box>
                                    );
                                }}>

                            <MenuItem value={'de'}>DE</MenuItem>
                            <MenuItem value={'en'}>EN</MenuItem>
                        </Select>
                        <IconButton onClick={() => window.open("https://www.gte.de/kmg-vision-cloud-anleitung")}
                                    sx={{color: '#fff'}} aria-label="Help">
                            <HelpIcon/>
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>
            <nav>
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: {xs: 'block', sm: 'none'},
                        '& .MuiDrawer-paper': {boxSizing: 'border-box', width: drawerWidth},
                    }}
                >
                    {drawer}
                </Drawer>
            </nav>
            <Box sx={{ flexGrow: 1, overflow: "auto", mt: 8 }}>
                {children}
            </Box>
        </Box>
    );
};
